import React from 'react';
import {
  ATMDivider,
  ATMForm,
  ATMGrid,
  ATMHeader,
  ATMSegment,
  ATMTable,
} from 'shared-it-appmod-ui';
import FieldReadOnly from 'src/components/atoms/field/field-readonly/field-readonly.component';
import FieldReadOnlyCommment from 'src/components/atoms/field/field-readonly-comment/field-readonly-comment.component';
import Lang from 'src/libraries/language';
import { IRequest } from 'src/models/request.model';
import FileManager from 'src/components/molecules/file-manager/file-manager.component';
import FlagReadOnly from 'src/components/atoms/flag-readonly/flag-readonly.component';
import FlightRequestPassengerInformation from '../flight-request-passenger-information/flight-request-passenger-information.component';
import FlightRequestContactInformation from '../flight-request-contact-information/flight-request-contact-information.component';
import styles from './flight-request-patrol-passenger.module.scss';
import FlightRequestCircuit from '../flight-request-circuit/flight-request-circuit.component';

type IProps = {
  data: IRequest;
};

const FlightRequestPatrolPassenger: React.FC<IProps> = ({
  data: { passengerFlight },
}) => {
  return (
    <ATMSegment
      color="yellow"
      className={passengerFlight?.isOldSCG ? styles.wrapperOld : styles.wrapper}
    >
      <ATMHeader>
        {!passengerFlight?.isOldSCG
          ? Lang.TTL_REQUEST_PATROL_PASSENGER_CONTRACTOR
          : Lang.TTL_REQUEST_PATROL_PASSENGER}
      </ATMHeader>

      <ATMForm>
        <ATMGrid columns={2}>
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_FLIGHT_TYPE}>
              {passengerFlight.flightType}
            </FieldReadOnly>
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_PROJECT}>
              {passengerFlight.project}
            </FieldReadOnly>
          </ATMGrid.Column>
          {passengerFlight.purpose && (
            <ATMGrid.Column>
              <FieldReadOnly
                label={Lang.LBL_PURPOSE}
                className={styles.wrapText}
              >
                {passengerFlight.purpose}
              </FieldReadOnly>
            </ATMGrid.Column>
          )}
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_FLIGHT_DISTRICT_DEPARTMENT}>
              {passengerFlight.flightDept}
            </FieldReadOnly>
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_PICKUP_LOCATION}>
              {passengerFlight.flightLocation}
            </FieldReadOnly>
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_FLIGHT_DESTINATION}>
              {passengerFlight.flightDestination}
            </FieldReadOnly>
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_Polygon}>
              {passengerFlight.polygon}
            </FieldReadOnly>
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_PASSENGER_COUNT}>
              {passengerFlight.passengerInfos?.length ?? 0}
            </FieldReadOnly>
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlightRequestCircuit data={passengerFlight.circuitInfos} />
          </ATMGrid.Column>
        </ATMGrid>

        <FlightRequestPassengerInformation
          data={passengerFlight.passengerInfos}
        />

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_OTHER_INFORMATION}
        </ATMHeader>

        <ATMGrid columns={1}>
          <ATMGrid.Column>
            <FlagReadOnly
              value={passengerFlight.nightFlag}
              label={Lang.LBL_NIGHT_FLIGHT}
            />
          </ATMGrid.Column>

          <ATMGrid.Column>
            <FlagReadOnly
              value={passengerFlight.sensitiveFlag}
              label={Lang.LBL_SENSITIVE_CUSTOMER}
            />

            {passengerFlight.sensitiveFlag &&
              (passengerFlight?.sensitiveFiles ?? []).length > 0 && (
                <FileManager
                  files={passengerFlight?.sensitiveFiles}
                  inline
                  editable={false}
                />
              )}
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={passengerFlight.culturalFlag}
              label={Lang.LBL_CULTURAL_RESTRICTION}
            />

            {passengerFlight.culturalFlag &&
              (passengerFlight?.culturalFiles ?? []).length > 0 && (
                <FileManager
                  files={passengerFlight?.culturalFiles}
                  inline
                  editable={false}
                />
              )}
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={passengerFlight.extraEquipmentFlag}
              label={Lang.LBL_EXTRA_EQUIPMENT}
            />

            {passengerFlight.extraEquipmentFlag && (
              <ATMTable>
                <ATMTable.Header>
                  <ATMTable.Row>
                    <ATMTable.HeaderCell>
                      {Lang.LBL_EQUIPMENT_TYPE}
                    </ATMTable.HeaderCell>
                    <ATMTable.HeaderCell>
                      {Lang.LBL_EQUIPMENT_WEIGHT}
                    </ATMTable.HeaderCell>
                  </ATMTable.Row>
                </ATMTable.Header>

                <ATMTable.Body>
                  {(passengerFlight?.equipmentInfos ?? []).map((item, key) => (
                    <ATMTable.Row key={`equip_${key}`}>
                      <ATMTable.Cell>{item?.type}</ATMTable.Cell>
                      <ATMTable.Cell>{item?.weight}</ATMTable.Cell>
                    </ATMTable.Row>
                  ))}
                </ATMTable.Body>
              </ATMTable>
            )}
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={passengerFlight.helmetFlag}
              label={Lang.LBL_HELMENT_SUIT}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={passengerFlight.hazmatFlag}
              label={Lang.LBL_HAZMAT}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={passengerFlight.basketFlag}
              label={Lang.LBL_BASKET_REQUIRED}
            />

            {passengerFlight.basketFlag && (
              <FieldReadOnly label={Lang.LBL_BASKET_WEIGHT} inline>
                {Lang.formatString(
                  Lang.LBL_POUNDS,
                  passengerFlight?.basketWeight ?? 0
                )}
              </FieldReadOnly>
            )}
          </ATMGrid.Column>
        </ATMGrid>
        <FlightRequestContactInformation data={passengerFlight} />
        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_FLIGHT_AREA}
        </ATMHeader>
        <ATMForm.Field>
          <FileManager
            files={passengerFlight.flightAreaFiles}
            editable={false}
            inline
          />
        </ATMForm.Field>
        <ATMDivider />

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_ADDITIONAL_COMMENT}
        </ATMHeader>

        <FieldReadOnlyCommment>
          {passengerFlight.passengerComments}
        </FieldReadOnlyCommment>
      </ATMForm>
    </ATMSegment>
  );
};

export default FlightRequestPatrolPassenger;
